import { LegalPageLayout } from './LegalPageLayout';

const LAST_UPDATED = 'May 26, 2026';

const sections = [
  { id: 'identity-contact', label: 'Identity and contact' },
  { id: 'data-we-collect', label: 'Data we collect' },
  { id: 'meta-data', label: 'Meta advertising data' },
  { id: 'purposes-basis', label: 'Purposes and basis' },
  { id: 'ai-analysis', label: 'AI analysis' },
  { id: 'retention-deletion', label: 'Retention and deletion' },
  { id: 'subprocessors-transfers', label: 'Subprocessors and transfers' },
  { id: 'rights', label: 'Your rights' },
  { id: 'security-cookies', label: 'Security and cookies' },
  { id: 'requests', label: 'Privacy requests' },
];

// Source IDs: S-EC-INFO, S-EURLEX-GDPR, S-META-BASIC-SETTINGS,
// S-ICO-PECR-COOKIES.
export function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      description="This policy explains the personal data and business data AdVerdict processes for creative analysis, Meta imports, and account operations."
      lastUpdated={LAST_UPDATED}
      sections={sections}
    >
      <section id="identity-contact" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Identity and contact
        </h2>
        <p>
          AdVerdict is operated by Hendrik Wels, Falkenried 74, 20251
          Hamburg, Germany. Hendrik Wels / AdVerdict is the controller for
          account, website, support, deletion, security, service administration,
          and legal-compliance handling. Privacy and deletion requests can be
          sent to Hendrik Wels at{' '}
          <a
            className="break-words text-brand hover:underline"
            href="mailto:hendrik.wels1@gmail.com"
          >
            hendrik.wels1@gmail.com
          </a>
          . AdVerdict has not appointed a data protection officer for the
          current solo indie-dev operation; this contact is the privacy contact,
          not a DPO contact.
        </p>
      </section>

      <section id="data-we-collect" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Data we collect
        </h2>
        <p>
          AdVerdict may collect account and authentication data, team and
          project metadata, uploaded creative files, import job records,
          performance metrics, analysis outputs, support messages, deletion
          request details, and operational logs. Some of this information may be
          personal data when it identifies a user, requester, advertiser,
          customer, actor, creator, or other person.
        </p>
      </section>

      <section id="meta-data" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Meta advertising data
        </h2>
        <p>
          When a team connects Meta, AdVerdict may process Meta user identity
          details, ad account identifiers and names, campaign/ad set/ad
          metadata, selected creative assets, thumbnails, Page-related media,
          performance metrics, and token status metadata. Access depends on the
          permissions granted by the Meta user and the team&apos;s authority to
          connect the relevant ad accounts or assets.
        </p>
      </section>

      <section id="purposes-basis" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Purposes and lawful basis
        </h2>
        <p>
          AdVerdict processes data to authenticate users, operate teams and
          projects, import and analyze creatives, generate reports and
          recommendations, secure the service, respond to support and privacy
          requests, comply with legal obligations, resolve disputes, and meet
          Meta platform requirements. Depending on context, processing may be
          based on contract necessity, legitimate interests, legal obligations,
          and user-authorized integration activity.
        </p>
      </section>

      <section id="ai-analysis" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">AI analysis</h2>
        <p>
          AdVerdict uses AI analysis to describe creative content, extract
          evidence, classify creative patterns, and produce decision-support
          outputs. AI analysis may support Scale/Fix/Kill recommendations, but
          it does not replace human review, legal review, platform policy
          review, or customer responsibility for advertising decisions.
        </p>
      </section>

      <section id="retention-deletion" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Retention and deletion
        </h2>
        <p>
          Account records, project metadata, uploaded/imported creatives, Meta
          connection metadata, analysis outputs, metrics, logs, and support or
          deletion request records are kept only as long as needed to provide
          AdVerdict, maintain accounts and teams, process uploads, imports, and
          analysis, comply with legal obligations, resolve disputes, and protect
          the service. Uploaded and imported creatives, Meta-derived data,
          metrics, and AI outputs are generally retained while the related
          account, team, project, or creative remains active, then deleted or
          anonymized according to the product deletion workflow unless a legal,
          security, backup, audit, or dispute reason requires limited retention.
          Project soft deletes are recoverable for 30 days before purge where
          the current product workflow applies. Meta tokens and connection
          metadata are removed or revoked on disconnect or deletion according to
          the approved workflow.
        </p>
      </section>

      <section id="subprocessors-transfers" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Subprocessors and international transfers
        </h2>
        <p>
          AdVerdict uses Amazon Web Services for hosting, storage,
          authentication, databases, queues, logs, secrets, APIs, CDN, and
          optional analytics infrastructure; Google Gemini / Google APIs for AI
          analysis of uploaded or imported creative content and related
          prompts/context; Google/Gmail for privacy, deletion, and support email
          handling while that inbox is used; and Meta Platforms APIs/CDN for
          user-authorized Meta OAuth, ad account access, creative import, media
          access, metrics/insights, and platform operations. Data may be
          transferred to or processed outside the European Union and the
          European Economic Area, including in the United States. Where required,
          AdVerdict relies on appropriate safeguards such as provider data
          processing agreements, EU Standard Contractual Clauses, and, where
          applicable, participation in the EU-U.S. Data Privacy Framework.
        </p>
      </section>

      <section id="rights" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">Your rights</h2>
        <p>
          Depending on your location and relationship to the data, you may have
          rights to access, correction, deletion, restriction, objection,
          portability, or withdrawal of consent where consent applies. EU or UK
          users may also have the right to lodge a complaint with a supervisory
          authority. California rights may apply if statutory thresholds are met.
        </p>
      </section>

      <section id="security-cookies" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Security, cookies, and similar storage
        </h2>
        <p>
          AdVerdict uses authentication, team isolation, access controls,
          encrypted AWS services, scoped secrets, presigned upload URLs, and
          operational monitoring to protect data. Current repository evidence
          found local and session storage for operational preferences and app
          state, with no direct cookie or third-party analytics tracker in the
          checked frontend paths. The cookies and storage position must be
          rechecked before final publication and whenever tracking changes.
        </p>
      </section>

      <section id="requests" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Privacy request contact path
        </h2>
        <p>
          To make a privacy request, use the deletion instructions at{' '}
          <a className="text-brand hover:underline" href="/data-deletion">
            /data-deletion
          </a>{' '}
          and include enough information to verify your account, team, project,
          Meta connection, or creative records. Privacy requests can also be
          sent to{' '}
          <a
            className="break-words text-brand hover:underline"
            href="mailto:hendrik.wels1@gmail.com"
          >
            hendrik.wels1@gmail.com
          </a>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}

export default PrivacyPolicy;
